/**
 * Cleaning for a `creator_id` that arrives in a URL.
 *
 * Why this exists (2026-10-06): our TikTok DMs put the Creator Studio /
 * onboarding link on its own line, followed by a blank line and a footer
 * ("Reply so these keep landing in your DMs"). TikTok's link detection pulls
 * the trailing text into the URL, so the loader received
 * `creator_id=findsbyjinghy\n\nReply so these keep landing in your DMs` and wrote
 * that whole string into `brand_creator_onboarding.creator_id` (109 rows, June →
 * October). Markdown-wrapped links (`handle](https://...)`) and trailing spaces
 * produced the same shape.
 *
 * Deliberately NOT a strict handle regex: live `brand_creators` holds real IDs
 * such as `YaYiTa❤️🌎` and `jemma’s`. We strip only the corruption we have seen —
 * everything after the first line break or markdown `](` — and trim.
 *
 * ⚠️ The cleanup SQL for already-written rows must apply this exact rule
 * (first line, cut at `](`, trim). A "first token" rule would turn
 * `hot mom energy` into `hot`, which is a different creator.
 */

const MAX_CREATOR_ID_LENGTH = 128;

/**
 * Returns the cleaned creator_id, or null when nothing usable is left
 * (absent, blank, or implausibly long after cleaning).
 */
export function normalizeCreatorIdParam(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const firstLine = raw.split(/[\r\n]/)[0] ?? "";
  const beforeMarkdown = firstLine.split("](")[0] ?? "";
  const cleaned = beforeMarkdown.trim();
  if (!cleaned || cleaned.length > MAX_CREATOR_ID_LENGTH) return null;
  return cleaned;
}

export interface ResolvedCreatorIdParam {
  /** Cleaned creator_id, or null when absent / unsalvageable. */
  creatorId: string | null;
  /**
   * Path + query of the same URL with the cleaned creator_id, set only when the
   * incoming value was dirty AND salvageable. Callers 302 to this so the
   * creator lands on a clean URL and every downstream route sees a clean ID.
   */
  redirectTo: string | null;
}

export function resolveCreatorIdParam(url: URL): ResolvedCreatorIdParam {
  const raw = url.searchParams.get("creator_id");
  const creatorId = normalizeCreatorIdParam(raw);

  if (raw == null || creatorId === null || creatorId === raw) {
    return { creatorId, redirectTo: null };
  }

  const fixed = new URL(url.toString());
  fixed.searchParams.set("creator_id", creatorId);
  return { creatorId, redirectTo: `${fixed.pathname}${fixed.search}` };
}
